// Regels voor de uitklap per persoon op het capaciteitsscherm.
//
// Joris (25 sep): "het voelt nog druk als ik items openklap". De uitklap had groepskoppen
// per prioriteit met gekleurde bolletjes, streepjes, ruitjes, een eigen dagrij, een
// totaalrij en een voetnoot. Nu: één regel per project (of losse taak), de uren op de
// dag waarop ze vallen, lege dagen leeg. De agenda is één regel onderaan.

import { computeDayLoad, effectiveTaskHours, taskProjectLabel, taskProjectDelen, DAGSTAAT } from '../../utils/capacityCalc';
import { formatDateISO } from '../../utils/weekUtils';
import { AFGEROND_SET } from '../../utils/taakStatus';

const soortVan = (t) => t?.prio || t?.status || '';

/** Een draaidag ís de dag — in de uitklap 'dag' in plaats van uren. */
export const isVolledagTaak = (t) => t?.type === 'Draaidag' || soortVan(t) === 'Draaidag';

/** Afwezig staat al in de bezettingsrij; een aparte regel herhaalt alleen. */
const isAfwezigTaak = (t) => t?.type === 'Vrije dag' || soortVan(t) === 'Vrije dag';

/**
 * Uren van een taak gelijk verdeeld over de werkdagen in zijn bereik → { iso: uren }
 * voor de gevraagde dagen. Let op: dit is de eenvoudige verdeling zonder opvullen of
 * afwezigheid; de uitklap zelf gebruikt de verdeling van computeDayLoad (zie
 * `uitklapRegels`), dit blijft voor de agenda-taak, de volle draaidag en de dag-tooltip.
 */
export function taskHoursPerDay(task, dayISOs, rules) {
  if (task?.hoursByDay) {
    const out = {};
    (dayISOs || []).forEach((d) => { if (task.hoursByDay[d]) out[d] = Number(task.hoursByDay[d]) || 0; });
    return out;
  }
  const s = String(task?.startDate || '').split('T')[0];
  const e = String(task?.endDate || task?.startDate || '').split('T')[0];
  const start = new Date(s);
  const end = new Date(e || s);
  if (Number.isNaN(start.getTime())) return {};
  const alle = [];
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) alle.push(formatDateISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  if (!alle.length) return {};
  const perDag = effectiveTaskHours(task, rules).val / alle.length;
  const out = {};
  (dayISOs || []).forEach((d) => { if (alle.includes(d)) out[d] = perDag; });
  return out;
}

/**
 * Tweede deel van de agendaregel. Twee onderwerpen aan elkaar geplakt werd afgekapt
 * ("Robin + Bram | Vo…"): bij één onderwerp de naam, bij meer het aantal afspraken
 * (per dag staan ze in de tooltip).
 */
export function agendaSub(t) {
  const events = Object.values(t?.agendaEvents || {}).flat();
  const onderwerpen = new Set(events.map((e) => String(e?.subject || 'Afspraak').trim()));
  if (onderwerpen.size > 1) return `${events.length} afspraken`;
  if (onderwerpen.size === 1) return [...onderwerpen][0];
  return t?.name === 'Meetings (agenda)' ? '' : (t?.name || '');
}

/** De agenda-taak (uit capaciteitPersonen) draagt de meeting-uren per dag. */
const isAgendaTaak = (t) => Boolean(t?.agendaEvents) || (t?.type === 'Afspraak' && Boolean(t?.hoursByDay));

/**
 * Per dag de uren per taak uit dezelfde berekening als de capaciteitsrij
 * (computeDayLoad): meerdaags werk vult eerst de ruimte die vast werk en meetings
 * overlaten, afwezige dagen krijgen niets en uitgesloten statussen tellen niet.
 * Eerst verdeelde deze uitklap elke taak gelijk over zijn dagen — dan stond er bij
 * do 6u vast werk + 10u montage do/vr in de rij 8/8 en in de uitklap 11/5 (Codex, 25 sep).
 * De agenda-uren (hoursByDay van de agenda-taak) zijn de meetings van de rij.
 */
function belastingVoor(taken, dayISOs, rules) {
  const cal = {};
  taken.filter(isAgendaTaak).forEach((t) => {
    Object.entries(t.hoursByDay || {}).forEach(([d, h]) => { cal[d] = (cal[d] || 0) + (Number(h) || 0); });
  });
  return computeDayLoad(taken, dayISOs || [], cal, rules);
}

/**
 * @returns {Array<{ sleutel, naam, sub, perDag: Object<string, number>, totaal: number,
 *   geschat: boolean, volledag: boolean, agenda: boolean, agendaEvents?: object, taken: object[] }>}
 */
export function uitklapRegels(tasks, dayISOs, rules) {
  const gezien = new Set();
  const uniek = (tasks || []).filter((t) => {
    if (!t) return false;
    if (t.id != null) {
      if (gezien.has(t.id)) return false;
      gezien.add(t.id);
    }
    return true;
  });
  // De berekening krijgt álles (ook de vrije dagen, die maken een dag 'vrij');
  // alleen de getoonde regels laten afgerond en afwezig weg.
  const load = belastingVoor(uniek, dayISOs, rules);
  const taken = uniek.filter((t) => !AFGEROND_SET.has(soortVan(t)) && !isAfwezigTaak(t));
  const vrij = (d) => load.staat[d] === DAGSTAAT.VRIJ;
  const zonderVrij = (m) => Object.fromEntries(Object.entries(m).filter(([d, h]) => h > 0 && !vrij(d)));

  const groepen = new Map();
  const agendaRegels = [];
  taken.forEach((t) => {
    const agenda = isAgendaTaak(t);
    const berekend = !agenda && load.perTaak.get(t);
    // Een volle draaidag heeft geen uren maar ís de dag ('dag' in de uitklap); een taak
    // die de berekening niet meetelt (uitgesloten status, afspraak) hoort hier ook niet.
    const volledag = !agenda && !berekend && isVolledagTaak(t);
    if (!agenda && !berekend && !volledag) return;
    const perDag = zonderVrij(berekend || taskHoursPerDay(t, dayISOs, rules));
    if (!Object.keys(perDag).length) return; // valt buiten de getoonde dagen
    if (agenda) {
      agendaRegels.push({
        sleutel: t.id || 'agenda', naam: 'Agenda', sub: agendaSub(t),
        perDag, totaal: Object.values(perDag).reduce((s, h) => s + h, 0),
        geschat: false, volledag: false, agenda: true, agendaEvents: t.agendaEvents, taken: [t],
      });
      return;
    }
    const project = taskProjectLabel(t);
    const vanalles = soortVan(t) === 'Vanallesuurtje';
    const sleutel = project
      ? `p:${project}${volledag ? ':dag' : ''}`
      : (vanalles ? 'vanalles' : `t:${t.id ?? t.name}`);
    if (!groepen.has(sleutel)) {
      // Naam = projectnummer + projectnaam; de klant is bijzaak en staat in de grijze
      // tweede helft (Joris, 26 sep: "alleen klantnaam is niet genoeg").
      const delen = project ? taskProjectDelen(t) : null;
      groepen.set(sleutel, {
        sleutel, naam: delen?.label || project || (vanalles ? 'Vanallesuurtjes' : (t.name || 'Taak')), klant: delen?.klant || '', sub: '',
        perDag: {}, totaal: 0, geschat: false, volledag, agenda: false, taken: [],
      });
    }
    const g = groepen.get(sleutel);
    g.taken.push(t);
    g.geschat = g.geschat || effectiveTaskHours(t, rules).estimated;
    Object.entries(perDag).forEach(([d, h]) => { g.perDag[d] = (g.perDag[d] || 0) + h; });
  });

  const regels = [...groepen.values()].map((g) => {
    const totaal = Object.values(g.perDag).reduce((s, h) => s + h, 0);
    let sub = '';
    if (g.naam !== 'Vanallesuurtjes' && g.sleutel.startsWith('p:')) {
      sub = [g.klant, g.taken.length === 1 ? (g.taken[0].name || '') : `${g.taken.length} taken`].filter(Boolean).join(' · ');
    } else if (g.naam === 'Vanallesuurtjes' && g.taken.length > 1) {
      sub = `${g.taken.length}×`;
    }
    return { ...g, totaal, sub };
  });
  const eersteDag = (r) => Object.keys(r.perDag).sort()[0] || '';
  regels.sort((a, b) => eersteDag(a).localeCompare(eersteDag(b)) || a.naam.localeCompare(b.naam, 'nl'));
  return [...regels, ...agendaRegels];
}

/** Uren per dag opgeteld over de regels (een hele draaidag telt hier niet als uren). */
export function dagTotalen(regels, dayISOs) {
  const out = {};
  (dayISOs || []).forEach((d) => {
    out[d] = (regels || []).reduce((s, r) => s + (r.volledag ? 0 : (r.perDag[d] || 0)), 0);
  });
  return out;
}
